import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Send, Copy, ShieldCheck, AlertTriangle, Layers, Mail, Grid3x3, Wand2, Cog, Zap } from "lucide-react";
import HelpBubble from "../components/HelpBubble";
import VideoTiersPanel from "../components/VideoTiersPanel";
import LaunchCampaignModal from "../components/LaunchCampaignModal";

const VERTICALS = ["finance", "insurance", "legal", "health", "home", "auto", "senior", "jobs"];
const STYLES = [
  { key: "ugly", label: "Ugly Ad (Clickbait)" },
  { key: "native", label: "Native Editorial" },
  { key: "modern", label: "Modern SaaS" },
];

export default function CampaignBuilder() {
  const [form, setForm] = useState({
    title: "",
    description: "",
    vertical: "finance",
    audience: "US adults 35-65",
    topics: "",
  });
  const [brief, setBrief] = useState(null);
  const [style, setStyle] = useState("ugly");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState({ brief: false, img: false, push: false, policy: false, variants: false, email: false, matrix: false });
  const [policy, setPolicy] = useState(null);
  const [variants, setVariants] = useState([]);
  const [pushForm, setPushForm] = useState({
    ad_account_id: "",
    page_id: "",
    landing_url: "",
    daily_budget: 20,
  });
  const [adAccounts, setAdAccounts] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [savedBriefs, setSavedBriefs] = useState([]);
  const [mode, setMode] = useState(() => localStorage.getItem("builder_mode") || "ai");
  const [showManualModal, setShowManualModal] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const loadSavedBriefs = async () => {
    try {
      const { data } = await api.get("/ai/briefs");
      setSavedBriefs(Array.isArray(data) ? data : []);
    } catch (e) { console.error("briefs load:", e); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/connections/status");
        setAdAccounts(data.fb_ad_accounts || []);
        if (data.fb_ad_accounts?.[0]) {
          setPushForm((p) => ({ ...p, ad_account_id: data.fb_ad_accounts[0].id }));
        }
      } catch {}
      loadSavedBriefs();
    })();
  }, []);

  // Accept a pre-generated brief passed via router state (e.g. from Scouting "Reserve first").
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const preloaded = location.state?.brief;
    if (preloaded && preloaded.brief_id) {
      setBrief(preloaded);
      setForm({
        title: preloaded.title || "",
        description: preloaded.description || "",
        vertical: preloaded.vertical || "finance",
        audience: preloaded.audience || "US adults 35-65",
        topics: (preloaded.topics || []).join(", "),
      });
      if (location.state?.note) toast.success(location.state.note);
      // Clear router state so a browser back/forward doesn't replay the toast
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitBrief = async (e) => {
    e?.preventDefault?.();
    if (!form.title || !form.description) {
      toast.error("Title + description required");
      return;
    }
    setBusy((b) => ({ ...b, brief: true }));
    setBrief(null);
    setImages([]);
    try {
      const { data } = await api.post("/ai/brief", {
        title: form.title,
        description: form.description,
        vertical: form.vertical,
        audience: form.audience,
        topics: form.topics.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setBrief(data);
      toast.success("Brief generated");
      loadSavedBriefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brief failed");
    } finally {
      setBusy((b) => ({ ...b, brief: false }));
    }
  };

  const genImages = async () => {
    if (!brief) { toast.error("Generate brief first"); return; }
    setBusy((b) => ({ ...b, img: true }));
    try {
      const prompt = `${brief.title}. ${brief.description}. Vertical: ${brief.vertical}. Hook: ${(brief.hooks || [])[0] || ""}`;
      const { data } = await api.post("/ai/images", {
        brief_id: brief.brief_id,
        prompt,
        style,
        count: 3,
      });
      setImages(data.image_urls || []);
      if (!data.image_urls?.length) toast.warning("No images returned, try again");
      else toast.success(`${data.image_urls.length} images generated`);
    } catch (e) {
      toast.error("Image gen failed");
    } finally {
      setBusy((b) => ({ ...b, img: false }));
    }
  };

  const pushToFB = async () => {
    if (!brief) return;
    if (!pushForm.ad_account_id || !pushForm.page_id || !pushForm.landing_url) {
      toast.error("Ad account, Page ID and Landing URL are required");
      return;
    }
    if (!videoUrl && images.length === 0) {
      toast.error("Generate images or a video first");
      return;
    }
    setBusy((b) => ({ ...b, push: true }));
    try {
      const { data } = await api.post("/ai/push-to-fb", {
        brief_id: brief.brief_id,
        ad_account_id: pushForm.ad_account_id,
        page_id: pushForm.page_id,
        daily_budget_cents: Math.round(parseFloat(pushForm.daily_budget || 20) * 100),
        image_urls: images,
        video_url: videoUrl || undefined,
        landing_url: pushForm.landing_url,
        objective: "OUTCOME_TRAFFIC",
      });
      if (data.success) {
        const kind = data.media_type === "video" ? "Video" : "Image";
        toast.success(`${kind} pushed. Campaign ${data.campaign_id} created (paused).`);
        if (data.note) toast.info(data.note);
        if (data.video_id) toast.info(`FB Video ID: ${data.video_id} · status ${data.video_status || "ready"}`);
      } else toast.error(data.error || "Push failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect Facebook first");
    } finally {
      setBusy((b) => ({ ...b, push: false }));
    }
  };

  const runPolicyCheck = async () => {
    if (!brief) return;
    setBusy((b) => ({ ...b, policy: true }));
    try {
      const { data } = await api.post("/ai/policy-check", {
        title: brief.title,
        description: brief.description,
        hooks: brief.hooks,
        ctas: brief.ctas,
        vertical: brief.vertical,
      });
      setPolicy(data);
      if (data.risk_level === "high") toast.error("High-risk policy issues detected");
      else if (data.risk_level === "medium") toast.warning("Medium risk — review before pushing");
      else toast.success("Policy check passed");
    } catch {
      toast.error("Policy check failed");
    } finally {
      setBusy((b) => ({ ...b, policy: false }));
    }
  };

  const buildVideo = undefined;  // video handlers live in VideoTiersPanel

  const genVariants = async () => {
    if (!brief) return;
    setBusy((b) => ({ ...b, variants: true }));
    try {
      const { data } = await api.post("/ai/variants", { brief_id: brief.brief_id, n: 6 });
      setVariants(data.variants || []);
      toast.success(`${data.variants?.length || 0} variants ready`);
    } catch {
      toast.error("Variant gen failed");
    } finally {
      setBusy((b) => ({ ...b, variants: false }));
    }
  };

  const sendDigest = async () => {
    setBusy((b) => ({ ...b, email: true }));
    try {
      const { data } = await api.post("/email/digest", {});
      if (data.sent) toast.success("Digest sent — check your inbox");
      else toast.error(data.error || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy((b) => ({ ...b, email: false }));
    }
  };

  const matrixLaunch = async () => {
    if (!brief) return;
    setBusy((b) => ({ ...b, matrix: true }));
    try {
      const { data } = await api.post("/optimize/matrix/launch", {
        brief_id: brief.brief_id,
        total_budget_usd: 30,
        ad_account_id: pushForm.ad_account_id || undefined,
        page_id: pushForm.page_id || undefined,
        landing_url: pushForm.landing_url || undefined,
        apply_exclusion: true,
      });
      toast.success(
        data.live
          ? `Matrix launched · ${data.launched_count}/${data.variant_count} paused on FB`
          : `Matrix dry-run saved · ${data.variant_count} variants (${data.live ? "LIVE" : "SIM"})`
      );
      if (data.exclusion_applied?.attempted) {
        toast.success(`Auto-excluded clickers on ${data.exclusion_applied.attached_count} adsets`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Matrix launch failed");
    } finally {
      setBusy((b) => ({ ...b, matrix: false }));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const loadBrief = (brief_id) => {
    if (!brief_id) return;
    const b = savedBriefs.find((x) => x.brief_id === brief_id);
    if (!b) return;
    setBrief(b);
    setForm({
      title: b.title || "",
      description: b.description || "",
      vertical: b.vertical || "finance",
      audience: b.audience || "US adults 35-65",
      topics: Array.isArray(b.topics) ? b.topics.join(", ") : (b.topics || ""),
    });
    setImages(b.image_urls || []);
    setVideoUrl(b.video_url || null);
    toast.success(`Loaded "${(b.title || b.brief_id).slice(0, 40)}"`);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem("builder_mode", newMode);
    if (newMode === "manual") setShowManualModal(true);
  };

  return (
    <div className="p-6 max-w-[1600px]" data-testid="builder-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">Campaign Builder <HelpBubble feature="brief" /></h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Pick your flow: let AI write the copy + paint the creatives, or fill out the full Facebook form yourself.
        </p>
      </div>

      {/* Mode selector */}
      <div
        data-testid="builder-mode-tabs"
        className="inline-flex bg-white border border-slate-200 rounded-md p-1 mb-4 shadow-sm"
      >
        <button
          onClick={() => switchMode("ai")}
          data-testid="builder-mode-ai"
          className={`flex items-center gap-2 text-sm font-medium rounded px-4 py-2 transition-all ${
            mode === "ai" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Wand2 className="w-3.5 h-3.5" /> AI-assisted
          <span className={`text-[10px] font-mono ml-1 ${mode === "ai" ? "text-indigo-100" : "text-slate-400"}`}>
            brief → copy → images → launch
          </span>
        </button>
        <button
          onClick={() => switchMode("manual")}
          data-testid="builder-mode-manual"
          className={`flex items-center gap-2 text-sm font-medium rounded px-4 py-2 transition-all ${
            mode === "manual" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Cog className="w-3.5 h-3.5" /> Manual form
          <span className={`text-[10px] font-mono ml-1 ${mode === "manual" ? "text-slate-300" : "text-slate-400"}`}>
            full Facebook campaign spec
          </span>
        </button>
      </div>

      {mode === "manual" && (
        <div
          data-testid="builder-manual-placeholder"
          className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-8 text-center"
        >
          <Zap className="w-8 h-8 mx-auto mb-3 text-slate-400" />
          <h3 className="text-base font-semibold text-slate-900 mb-1">Full Facebook campaign form</h3>
          <p className="text-xs text-slate-500 font-mono mb-4 max-w-md mx-auto">
            Set objective, daily budget, countries, age range, CTA, and creative mix.
            Save as a template to launch 5 variants in 5 clicks next time.
          </p>
          <button
            onClick={() => setShowManualModal(true)}
            data-testid="builder-open-manual"
            className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-medium rounded-md px-5 py-2.5 inline-flex items-center gap-2"
          >
            <Zap className="w-4 h-4" /> Open launch form
          </button>
          <p className="text-[10px] text-slate-400 font-mono mt-3">
            Campaigns are created in <span className="text-amber-600">PAUSED</span> state so you can verify before spend starts.
          </p>
        </div>
      )}

      <LaunchCampaignModal
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
      />

      {mode === "ai" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: form */}
        <form onSubmit={submitBrief} className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          {savedBriefs.length > 0 && (
            <div
              data-testid="load-brief-picker"
              className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
            >
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono shrink-0">
                Load existing
              </label>
              <select
                data-testid="load-brief-select"
                defaultValue=""
                onChange={(e) => { loadBrief(e.target.value); e.target.value = ""; }}
                className="flex-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40 truncate"
                title="Skip the LLM call — reuse any brief you've already generated"
              >
                <option value="">Pick a saved brief… ({savedBriefs.length} available)</option>
                {savedBriefs.map((b) => (
                  <option key={b.brief_id} value={b.brief_id}>
                    {(b.title || b.brief_id).slice(0, 55)}
                    {b.vertical ? ` · ${b.vertical}` : ""}
                    {b.created_at ? ` · ${new Date(b.created_at).toLocaleDateString()}` : ""}
                  </option>
                ))}
              </select>
              <span className="text-[10px] text-slate-400 font-mono shrink-0">no LLM call</span>
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Campaign Title</label>
            <input
              data-testid="brief-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Debt Relief Quiz"
              className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">What is this campaign about?</label>
            <textarea
              data-testid="brief-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Help Americans consolidate high-interest credit card debt into a single low-rate program…"
              rows={4}
              className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Vertical</label>
              <select
                data-testid="brief-vertical"
                value={form.vertical}
                onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              >
                {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Audience</label>
              <input
                data-testid="brief-audience"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Article topics (comma separated)</label>
            <input
              data-testid="brief-topics"
              value={form.topics}
              onChange={(e) => setForm({ ...form, topics: e.target.value })}
              placeholder="debt consolidation, savings account, credit score"
              className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            />
          </div>
            <button
              type="submit"
              disabled={busy.brief}
              data-testid="generate-brief-btn"
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
            <Sparkles className="w-4 h-4" /> {busy.brief ? "Generating…" : "Generate Brief"}
          </button>
        </form>

        {/* Right: result */}
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="brief-output">
          {!brief && (
            <div className="flex items-center justify-center text-slate-500 text-sm min-h-[300px] border border-dashed border-slate-200 rounded-md">
              Brief output will appear here.
            </div>
          )}
          {brief && (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Hooks</div>
                <div className="space-y-1.5">
                  {(brief.hooks || []).map((h, i) => (
                    <div key={`${h}-${i}`} className="flex items-start gap-2 text-sm text-slate-900 bg-white border border-slate-200 rounded p-2" data-testid={`hook-${i}`}>
                      <span className="font-mono text-[10px] text-slate-500 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1">{h}</span>
                      <button onClick={() => copy(h)} className="text-slate-500 hover:text-slate-900"><Copy className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">CTAs</div>
                  {(brief.ctas || []).map((c, i) => (
                    <div key={`${c}-${i}`} className="text-xs text-slate-900 bg-white border border-slate-200 rounded px-2 py-1 mb-1 font-medium" data-testid={`cta-${i}`}>{c}</div>
                  ))}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2 flex items-center gap-2">
                    RSOC forceKey
                    {(brief.promoted_keywords || []).length > 0 && (
                      <span
                        data-testid="brief-promoted-badge"
                        className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300 normal-case tracking-normal"
                        title="These keywords were promoted from the Intelligence → Keywords tab based on real S1 RPC data."
                      >
                        ★ {brief.promoted_keywords.length} promoted from last sync
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1" data-testid="keywords-list">
                    {(brief.keywords || []).map((k, i) => {
                      const isPromoted = (brief.promoted_keywords || []).map((x) => x.toLowerCase()).includes(k.toLowerCase());
                      return (
                        <span
                          key={`${k}-${i}`}
                          className={`text-[11px] rounded px-1.5 py-0.5 font-mono border ${
                            isPromoted
                              ? "bg-amber-50 text-amber-800 border-amber-300"
                              : "bg-blue-500/10 text-blue-700 border-blue-500/20"
                          }`}
                          title={isPromoted ? "Promoted from high-RPC S1 data" : undefined}
                        >
                          {isPromoted && <span className="mr-0.5">★</span>}{k}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {(brief.ad_copy || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Ad Copy Variants</div>
                  <div className="space-y-2">
                    {brief.ad_copy.map((a, i) => (
                      <div key={`${a.headline || ""}-${i}`} className="bg-white border border-slate-200 rounded p-2.5 text-xs">
                        <div className="text-slate-900 mb-1">{a.primary_text}</div>
                        <div className="flex gap-3 text-[11px] text-slate-500 font-mono">
                          <span>hl: <span className="text-slate-700">{a.headline}</span></span>
                          <span>·</span>
                          <span>desc: <span className="text-slate-700">{a.description}</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image gen */}
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Creatives</div>
                  <div className="flex items-center gap-2">
                    <select
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      data-testid="image-style"
                      className="bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
                    >
                      {STYLES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <button
                      onClick={genImages}
                      disabled={busy.img}
                      data-testid="generate-images-btn"
                      className="flex items-center gap-1 bg-white border border-slate-200 hover:bg-slate-100 rounded-md px-2.5 py-1 text-xs disabled:opacity-60"
                    >
                      <ImageIcon className="w-3 h-3" /> {busy.img ? "Painting…" : "Generate"}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="images-grid">
                  {images.length === 0 && (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="aspect-square bg-slate-50 border border-dashed border-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400 font-mono">empty</div>
                    ))
                  )}
                  {images.map((u, i) => (
                    <img key={u} src={`${BACKEND}${u}`} alt="" className="aspect-square object-cover rounded border border-slate-200" data-testid={`image-${i}`} />
                  ))}
                </div>
              </div>

              <button
                onClick={runPolicyCheck}
                disabled={busy.policy}
                data-testid="policy-check-btn"
                className="w-full flex items-center justify-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
              >
                <ShieldCheck className="w-4 h-4" /> {busy.policy ? "Checking…" : "Pre-flight policy check"}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={genVariants}
                  disabled={busy.variants}
                  data-testid="gen-variants-btn"
                  className="flex items-center justify-center gap-1 bg-white border border-slate-200 hover:bg-slate-50 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60"
                >
                  <Layers className="w-3.5 h-3.5" /> {busy.variants ? "…" : "A/B variants"}
                </button>
                <button
                  onClick={sendDigest}
                  disabled={busy.email}
                  data-testid="send-digest-btn"
                  className="flex items-center justify-center gap-1 bg-white border border-slate-200 hover:bg-slate-50 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60"
                >
                  <Mail className="w-3.5 h-3.5" /> {busy.email ? "Sending…" : "Email digest"}
                </button>
              </div>

              {/* AI Video — all generation, upload, and remix UI lives here */}
              <VideoTiersPanel brief={brief} images={images} BACKEND={BACKEND} onVideoChange={setVideoUrl} />
              {videoUrl && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-[11px] text-emerald-700 font-mono flex items-center gap-2" data-testid="video-attached-banner">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                  Video attached · will push as video creative
                </div>
              )}

              {variants.length > 0 && (
                <div className="space-y-1.5" data-testid="variants-list">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">A/B Variants ({variants.length})</div>
                  {variants.map((v, i) => (
                    <div key={`${v.angle}-${v.title}-${i}`} className="bg-white border border-slate-200 rounded p-2 text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono uppercase text-blue-700 bg-blue-500/10 border border-blue-500/20 rounded px-1.5 py-0.5">{v.angle}</span>
                        <span className="text-slate-700 font-medium">{v.title}</span>
                      </div>
                      <div className="text-slate-500">{v.primary_hook}</div>
                    </div>
                  ))}
                </div>
              )}
              {policy && (
                <div
                  data-testid="policy-result"
                  className={`rounded-md p-3 text-xs border ${
                    policy.risk_level === "high"
                      ? "bg-red-500/10 border-red-500/30 text-red-700"
                      : policy.risk_level === "medium"
                      ? "bg-amber-500/10 border-amber-500/30 text-amber-700"
                      : "bg-emerald-500/10 border-emerald-500/30 text-emerald-700"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1 font-mono uppercase tracking-wider text-[10px]">
                    {policy.risk_level === "low" ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    Risk: {policy.risk_level} · {policy.approved ? "approved" : "rejected"}
                  </div>
                  <div className="text-slate-900/90 mb-1">{policy.notes}</div>
                  {(policy.issues || []).map((it, i) => (
                    <div key={`${it.severity}-${it.text}-${i}`} className="mt-1">
                      <span className="font-mono text-[10px] uppercase">[{it.severity}]</span>{" "}
                      <span>{it.text}</span>{" "}
                      {it.fix && <span className="text-slate-900/70">→ {it.fix}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Push-to-FB fields */}
              <div className="pt-3 border-t border-slate-200 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Launch to Facebook</div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={pushForm.ad_account_id}
                    onChange={(e) => setPushForm({ ...pushForm, ad_account_id: e.target.value })}
                    data-testid="push-ad-account"
                    className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
                  >
                    <option value="">Select ad account…</option>
                    {adAccounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.id})</option>
                    ))}
                  </select>
                  <input
                    value={pushForm.page_id}
                    onChange={(e) => setPushForm({ ...pushForm, page_id: e.target.value })}
                    placeholder="Page ID (e.g. 409845705534339)"
                    data-testid="push-page-id"
                    className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
                  />
                </div>
                <input
                  value={pushForm.landing_url}
                  onChange={(e) => setPushForm({ ...pushForm, landing_url: e.target.value })}
                  placeholder="Landing URL (your RSOC page w/ tracking params)"
                  data-testid="push-landing-url"
                  className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
                />
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-slate-500 font-mono">Daily budget $</label>
                  <input
                    type="number"
                    value={pushForm.daily_budget}
                    onChange={(e) => setPushForm({ ...pushForm, daily_budget: e.target.value })}
                    data-testid="push-budget"
                    className="w-20 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
                  />
                </div>
              </div>

              <button
                onClick={pushToFB}
                disabled={busy.push || (images.length === 0 && !videoUrl)}
                data-testid="push-to-fb-btn"
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {busy.push ? "Pushing…" : videoUrl ? "Push video to Facebook (paused)" : "Push to Facebook (paused)"}
              </button>
              <button
                onClick={matrixLaunch}
                disabled={busy.matrix}
                data-testid="matrix-launch-inline-btn"
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
              >
                <Grid3x3 className="w-4 h-4" /> {busy.matrix ? "Launching 24 variants…" : "Matrix launch (24 variants @ $30 test)"}
              </button>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
